import { inject, Injectable } from '@angular/core';
import { Document } from '../models/document.model';
import { SessionsService } from '../modules/besharp-sessions/lib/services/sessions.service';
import { buildSessionHeaders } from '../modules/besharp-sessions/lib/utils/interceptor';
import { environment } from '../../environment/environment';
import { StatusEnumDocument } from '../enum/status-document.enum';

@Injectable({ providedIn: 'root' })
export class IngestionApiService {
  constructor() {}

  private sessionsService = inject(SessionsService);
  private readonly baseUrl = environment.backend.endpoint || '/api';

  async getDocuments(): Promise<Document[]> {
    try {
      const response = await this.requestJson<any>('/admin/ingestion/documents', { method: 'GET' });

      if (response && !Array.isArray(response)) {
        return response.documents || [];
      }
      return response || [];
    } catch (error) {
      console.error('Error fetching documents:', error);
      throw error;
    }
  }

  async getDocumentPresignedUrl(document_id: string): Promise<{ presigned_url: string }> {
    try {
      return await this.requestJson<{ presigned_url: string }>(`/admin/ingestion/document/${document_id}`, {
        method: 'GET',
      });
    } catch (error) {
      console.error(`Error fetching the presigned URL for document ${document_id}:`, error);
      throw error;
    }
  }

  async uploadDocument(document: Document, contentType: string): Promise<{ presigned_url: string }> {
    try {
      return await this.requestJson<{ document: Document; presigned_url: string }>('/admin/ingestion/document', {
        method: 'POST',
        body: JSON.stringify({
          document: document,
          content_type: contentType,
        }),
      });
    } catch (error) {
      console.error('Error uploading the document:', error);
      throw error;
    }
  }

  async updateDocument(document: Document): Promise<{ document: Document }> {
    try {
      return await this.requestJson<{ document: Document }>('/admin/ingestion/document', {
        method: 'PATCH',
        body: JSON.stringify({
          document: document,
          status: StatusEnumDocument.UPDATING,
        }),
      });
    } catch (error) {
      console.error('Error updating the document:', error);
      throw error;
    }
  }

  async deleteDocument(document_id: string): Promise<{ message: string }> {
    try {
      return await this.requestJson<{ message: string }>(`/admin/ingestion/document/${document_id}`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.error(`Error deleting document ${document_id}:`, error);
      throw error;
    }
  }

  async downloadDocument(presigned_url: string): Promise<void> {
    try {
      const response = await fetch(presigned_url, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`Error downloading file from presigned URL (${response.status})`);
      }

      const blob = await response.blob();

      const urlObj = new URL(presigned_url);
      const filename = urlObj.pathname.split('/').pop() || 'document_downloaded';

      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading document from presigned URL:', error);
      throw error;
    }
  }

  private async requestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
    const authHeaders = await buildSessionHeaders(this.sessionsService);

    const headers = new Headers(init.headers);
    headers.set('Content-Type', 'application/json');
    for (const [key, value] of Object.entries(authHeaders)) {
      headers.set(key, value);
    }

    const response = await fetch(`${this.baseUrl}${path}`, {
      ...init,
      headers,
      credentials: 'include',
    });

    if (response.status === 401 || response.status === 403) {
      throw new Error('Unauthorized - please sign in again');
    }

    if (!response.ok) {
      throw new Error(`Request failed (${response.status})`);
    }

    return (await response.json()) as T;
  }
}
